/**
 * Hooks de dados do módulo LST (React Query).
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LstService, weekDates, type LstTemplateInput } from '../services/lstService';
import type {
  LstCargo,
  LstExecucao,
  LstLayerAction,
  LstLayerMeeting,
  LstLayerMeetingKey,
  LstMilestone,
  LstRevisaoAta,
  LstStatus,
  LstTemplate,
} from '../types';

const STALE = 60_000;

export const lstKeys = {
  templates: (orgId: string) => ['lst', 'templates', orgId] as const,
  execucao: (orgId: string, userIds: string[], start: string, end: string) =>
    ['lst', 'execucao', orgId, [...userIds].sort().join(','), start, end] as const,
};

/** Padrões de rotina ativos da organização. */
export function useLstTemplates(organizationId: string) {
  return useQuery<LstTemplate[]>({
    queryKey: lstKeys.templates(organizationId),
    queryFn: () => LstService.listTemplates(organizationId),
    enabled: !!organizationId,
    staleTime: STALE,
  });
}

/** Execuções de um conjunto de usuários num intervalo. */
export function useLstExecucao(
  organizationId: string,
  userIds: string[],
  startDate: string,
  endDate: string,
) {
  return useQuery<LstExecucao[]>({
    queryKey: lstKeys.execucao(organizationId, userIds, startDate, endDate),
    queryFn: () => LstService.listExecucao(organizationId, userIds, startDate, endDate),
    enabled: !!organizationId && userIds.length > 0,
    staleTime: STALE,
  });
}

export interface UpsertExecucaoVars {
  organization_id: string;
  template_id: string;
  user_id: string;
  data: string;
  status: LstStatus;
  justificativa_desvio?: string | null;
  action_id?: string | null;
  observacao?: string | null;
  anexo_url?: string | null;
  indicator_realized_value?: number | null;
}

/** Histórico recente de uma rotina por um usuário. */
export function useTemplateHistory(
  organizationId: string,
  templateId: string | null,
  userId: string,
  limit = 5,
) {
  return useQuery<LstExecucao[]>({
    queryKey: ['lst', 'history', organizationId, templateId, userId, limit],
    queryFn: () => LstService.listTemplateHistory(organizationId, templateId as string, userId, limit),
    enabled: !!organizationId && !!templateId && !!userId,
    staleTime: STALE,
  });
}

/** Histórico org-wide (qualquer operador) — Memória de Campo / Kaizen. */
export function useTemplateOrgHistory(
  organizationId: string,
  templateId: string | null,
  limit = 5,
) {
  return useQuery<LstExecucao[]>({
    queryKey: ['lst', 'org-history', organizationId, templateId, limit],
    queryFn: () => LstService.listTemplateOrgHistory(organizationId, templateId as string, limit),
    enabled: !!organizationId && !!templateId,
    staleTime: STALE,
  });
}

/** Mutation para registrar/atualizar a execução de uma rotina. */
export function useUpsertExecucao(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpsertExecucaoVars) => LstService.upsertExecucao(vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lst', 'execucao', organizationId] });
      qc.invalidateQueries({ queryKey: ['lst', 'history', organizationId] });
      qc.invalidateQueries({ queryKey: ['lst', 'org-history', organizationId] });
    },
  });
}

/** Datas (Seg→Dom) da semana corrente — memoizado por dia. */
export function useCurrentWeek(): { dates: string[]; start: string; end: string } {
  return useMemo(() => {
    const dates = weekDates(new Date());
    return { dates, start: dates[0], end: dates[dates.length - 1] };
  }, []);
}

function invalidateTemplates(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  qc.invalidateQueries({ queryKey: lstKeys.templates(orgId) });
}

/** Cria/atualiza/desativa rotinas padrão (Configuração → Bloco A). */
export function useTemplateMutations(organizationId: string) {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: (input: LstTemplateInput) => LstService.createTemplate(input),
    onSuccess: () => invalidateTemplates(qc, organizationId),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; patch: Partial<LstTemplateInput> }) =>
      LstService.updateTemplate(vars.id, vars.patch),
    onSuccess: () => invalidateTemplates(qc, organizationId),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => LstService.deactivateTemplate(id),
    onSuccess: () => invalidateTemplates(qc, organizationId),
  });

  return { create, update, deactivate };
}

/** Atas de revisão do padrão. */
export function useLstRevisoes(organizationId: string, enabled = true) {
  return useQuery<LstRevisaoAta[]>({
    queryKey: ['lst', 'revisoes', organizationId],
    queryFn: () => LstService.listRevisoes(organizationId),
    enabled: !!organizationId && enabled,
    staleTime: STALE,
  });
}

/** Milestones para marcos nos gráficos de tendência. */
export function useLstMilestones(
  organizationId: string,
  targetRole?: LstCargo,
  startDate?: string,
  endDate?: string,
  enabled = true,
) {
  return useQuery<LstMilestone[]>({
    queryKey: ['lst', 'milestones', organizationId, targetRole, startDate, endDate],
    queryFn: () => LstService.listMilestones(organizationId, targetRole, startDate, endDate),
    enabled: !!organizationId && enabled,
    staleTime: STALE,
  });
}

export function useCreateRevisao(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      target_role: LstCargo;
      liderado_user_id?: string | null;
      reviewer_user_id: string;
      notes: string;
      label?: string;
    }) =>
      LstService.createRevisaoWithMilestone({
        organization_id: organizationId,
        ...vars,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lst', 'revisoes', organizationId] });
      qc.invalidateQueries({ queryKey: ['lst', 'milestones', organizationId] });
    },
  });
}

/** Reuniões de camada estruturadas (aba Camada). */
export function useLstLayerMeetings(
  organizationId: string,
  filters?: {
    leaderUserId?: string;
    subordinateUserId?: string;
    limit?: number;
  },
  enabled = true,
) {
  return useQuery<LstLayerMeeting[]>({
    queryKey: [
      'lst',
      'layer-meetings',
      organizationId,
      filters?.leaderUserId,
      filters?.subordinateUserId,
      filters?.limit,
    ],
    queryFn: () => LstService.listLayerMeetings(organizationId, filters),
    enabled: !!organizationId && enabled,
    staleTime: STALE,
  });
}

export function useCreateLayerMeeting(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      meeting_date?: string;
      layer_key: LstLayerMeetingKey;
      leader_user_id: string;
      subordinate_user_id: string;
      leader_cargo: LstCargo;
      subordinate_cargo: LstCargo;
      adherence_pct?: number | null;
      duration_minutes?: number | null;
      sections: Record<string, string>;
      actions: LstLayerAction[];
      evidence_urls?: string[];
      created_by?: string | null;
    }) =>
      LstService.createLayerMeeting({
        organization_id: organizationId,
        ...vars,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lst', 'layer-meetings', organizationId] });
    },
  });
}

/** Define o gestor direto/avaliador de um usuário (Configuração → Bloco B). */
export function useSetReportsTo(organizationId: string) {
  return useMutation({
    mutationFn: (vars: { userId: string; reportsToUserId: string | null }) =>
      LstService.setReportsTo(organizationId, vars.userId, vars.reportsToUserId),
  });
}
